import React, { useState, useEffect } from "react";
import {
    Box, Button, TextField, Typography, Grid, IconButton,
    Divider, Paper, Tooltip, Dialog, DialogTitle, DialogContent,
    DialogActions, CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ImageIcon from "@mui/icons-material/Image";
import DeleteIcon from "@mui/icons-material/Delete";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

const ViewSoImageModal = ({ closePopup, modalData }) => {
    const [oldData, setOldData] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [verifyingId, setVerifyingId] = useState(null);

    const [disputeModal, setDisputeModal] = useState({
        open: false,
        entryId: null,
        item: null,
        remark: "",
        remarkError: "",
        submitting: false,
    });

    // ================= GET OLD DATA =================
    useEffect(() => {
        getOldData();
    }, []);

    const getOldData = async () => {
        try {
            if (!modalData?.regId) return;
            setHistoryLoading(true);
            const result = await window.Platform.database.getSOBOforOlympiadByRegId({
                regId: modalData.regId,
            });
            if (result?.status) {
                setOldData(result?.data || []);
            }
            else {
                window.NotificationUtils.showError(result?.message || "Failed to fetch data");
            }
            // setOldData(result?.status ? result?.data || [] : []);
        } catch (error) {
            console.log(error);
            setOldData([]);
        } finally {
            setHistoryLoading(false);
        }
    };

    const handleVerify = async (item) => {
        const isSuccess = window?.confirm("Are you sure to verify this SO")
        if (!isSuccess) return;
        try {
            setVerifyingId(item?.id);

            const result = await window.Platform.database.updateSOBOStatus({
                sobo_id: item?.id,
                action: "verify"
            });

            if (result?.status) {
                window.NotificationUtils.showSuccess(result?.message || "Entry verified successfully");
                // getOldData();
                closePopup()
            } else {
                window.NotificationUtils.showError(result?.message || "Something went wrong");
            }
        } catch (error) {
            console.log(error);
            window.NotificationUtils.showError("Something went wrong");
        } finally {
            setVerifyingId(null);
        }
    };

    const openDisputeModal = (item) => {
        setDisputeModal({
            open: true,
            entryId: item?.id,
            item,
            remark: "",
            remarkError: "",
            submitting: false,
        });
    };

    const closeDisputeModal = () => {
        setDisputeModal({
            open: false,
            entryId: null,
            item: null,
            remark: "",
            remarkError: "",
            submitting: false,
        });
    };

    const handleDisputeRemarkChange = (e) => {
        const val = e.target.value;
        setDisputeModal((prev) => ({
            ...prev,
            remark: val,
            // clear error as soon as they type enough
            remarkError: val.trim().replace(/\s+/g, " ").length < 20
                ? `Remark must be at least 20 characters (${val.trim().replace(/\s+/g, " ").length}/20)`
                : "",
        }));
    };

    const handleDisputeSubmit = async () => {
        const trimmed = disputeModal.remark.trim();

        if (!trimmed) {
            setDisputeModal((prev) => ({
                ...prev,
                remarkError: "Remark is required",
            }));
            return;
        }

        if (trimmed.length < 20) {
            setDisputeModal((prev) => ({
                ...prev,
                remarkError: `Remark must be at least 20 characters (${trimmed.length}/20)`,
            }));
            return;
        }

        try {
            setDisputeModal((prev) => ({ ...prev, submitting: true }));

            const result = await window.Platform.database.updateSOBOStatus({
                sobo_id: disputeModal.entryId,
                action: "despute",
                despute_remark: trimmed
            });

            if (result?.status) {
                window.NotificationUtils.showSuccess(result?.message || "Discrepancy raised successfully");
                closeDisputeModal();
                // getOldData();
                closePopup();
            } else {
                window.NotificationUtils.showError(result?.message || "Something went wrong");
                setDisputeModal((prev) => ({ ...prev, submitting: false }));
            }
        } catch (error) {
            console.log(error);
            window.NotificationUtils.showError("Something went wrong");
            setDisputeModal((prev) => ({ ...prev, submitting: false }));
        }
    };

    const AttachmentGrid = ({ files, onRemove, height = "140px" }) => (
        <Box mt={2}>
            <Grid container spacing={2}>
                {files.map((att, index) => {
                    const src = att?.preview || att?.path || null;

                    const isImage =
                        att?.type?.startsWith("image") ||
                        /\.(jpg|jpeg|png|gif|webp)/i.test(att?.path || "");

                    const isPdf =
                        att?.type === "application/pdf" ||
                        /\.pdf$/i.test(att?.path || att?.name || "");

                    return (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                            <Paper
                                elevation={2}
                                sx={{
                                    p: 1,
                                    borderRadius: 2,
                                    position: "relative",
                                    overflow: "hidden",
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                {onRemove && (
                                    <Tooltip title="Remove">
                                        <IconButton
                                            size="small"
                                            onClick={() => onRemove(index)}
                                            sx={{ position: "absolute", top: 5, right: 5, zIndex: 2, background: "#fff" }}
                                        >
                                            <DeleteIcon fontSize="small" color="error" />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                {/* Zoom for images | Open for PDFs */}
                                {att?.path && (
                                    <Tooltip title={isPdf ? "Open PDF" : "Preview"}>
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                isPdf
                                                    ? window.open(att.path, "_blank")
                                                    : setPreviewImage(att.path)
                                            }
                                            sx={{ position: "absolute", top: 5, left: 5, zIndex: 2, background: "#fff" }}
                                        >
                                            {isPdf
                                                ? <PictureAsPdfIcon fontSize="small" color="error" />
                                                : <ZoomInIcon fontSize="small" color="primary" />
                                            }
                                        </IconButton>
                                    </Tooltip>
                                )}

                                {isImage ? (
                                    <img
                                        src={src}
                                        alt={att?.originalName || att?.name || "attachment"}
                                        onClick={() => src && setPreviewImage(src)}
                                        style={{
                                            width: "100%",
                                            height,
                                            objectFit: "cover",
                                            borderRadius: "8px",
                                            cursor: "pointer",
                                        }}
                                    />
                                ) : isPdf ? (
                                    src?.startsWith("blob:") ? (
                                        // local newly selected PDF → iframe preview
                                        <Box
                                            sx={{ height, borderRadius: "8px", overflow: "hidden", cursor: "pointer" }}
                                            onClick={() => window.open(src, "_blank")}
                                        >
                                            <iframe
                                                src={src}
                                                title={att?.name || "PDF"}
                                                style={{ width: "100%", height: "100%", border: "none", pointerEvents: "none" }}
                                            />
                                        </Box>
                                    ) : (
                                        // server PDF → icon + click to open
                                        <Box
                                            onClick={() => att?.path && window.open(att.path, "_blank")}
                                            sx={{
                                                height,
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                background: "#fff3f3",
                                                borderRadius: "8px",
                                                cursor: "pointer",
                                                border: "1px solid #ffcccc",
                                            }}
                                        >
                                            <PictureAsPdfIcon sx={{ fontSize: 50, color: "#e53935" }} />
                                            <Typography variant="caption" sx={{ color: "#e53935", mt: 0.5, fontWeight: 600 }}>
                                                Open PDF
                                            </Typography>
                                        </Box>
                                    )
                                ) : (
                                    // other files (doc, xls, etc.)
                                    <Box
                                        sx={{
                                            height,
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            background: "#f5f5f5",
                                            borderRadius: "8px",
                                        }}
                                    >
                                        <ImageIcon sx={{ fontSize: 50, color: "#999" }} />
                                        <Typography variant="caption" sx={{ color: "#999", mt: 0.5 }}>
                                            {att?.name?.split(".").pop()?.toUpperCase() || "File"}
                                        </Typography>
                                    </Box>
                                )}

                                <Typography
                                    variant="caption"
                                    sx={{ mt: 1, display: "block", textAlign: "center", wordBreak: "break-word" }}
                                >
                                    {att?.originalName || att?.name || att?.fileName || "File"}
                                </Typography>

                                <Button
                                    variant="contained"
                                    size="small"
                                    fullWidth
                                    sx={{ mt: 1 }}
                                    onClick={async () => {
                                        try {
                                            const fileUrl = att?.path
                                                ? att.path
                                                : `${window.Platform.database.funcForBaseURL()}/file/${att.fileName}`;
                                            const response = await fetch(fileUrl);
                                            if (!response.ok) throw new Error("Failed to fetch");
                                            const blob = await response.blob();
                                            const url = window.URL.createObjectURL(blob);
                                            const link = document.createElement("a");
                                            link.href = url;
                                            // link.setAttribute("download", att?.originalName || att?.fileName || `file_${index}`);
                                            const fileName =att?.originalName || att?.fileName || `file_${index}`;

                                            link.setAttribute(
                                                "download",
                                                `SO_${modalData?.rno_id}_${fileName}`
                                            );
                                            document.body.appendChild(link);
                                            link.click();
                                            link.remove();
                                            window.URL.revokeObjectURL(url);
                                        } catch (err) {
                                            console.error(err);
                                            window.NotificationUtils.showError("Failed to download file");
                                        }
                                    }}
                                >
                                    ⬇ Download
                                </Button>
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
    // ================= IMAGE FULLSCREEN PREVIEW =================
    const ImagePreviewOverlay = () =>
        previewImage ? (
            <Box
                onClick={() => setPreviewImage(null)}
                sx={{
                    position: "fixed",
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: "rgba(0,0,0,0.85)",
                    zIndex: 9999,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "zoom-out",
                }}
            >
                <IconButton
                    onClick={() => setPreviewImage(null)}
                    sx={{ position: "absolute", top: 16, right: 16, color: "#fff" }}
                >
                    <CloseIcon />
                </IconButton>
                <img
                    src={previewImage}
                    alt="preview"
                    style={{ maxWidth: "90%", maxHeight: "90%", borderRadius: 8, objectFit: "contain" }}
                />
            </Box>
        ) : null;

    // ================= RENDER =================
    return (
        <Box p={2} position="relative">
            <ImagePreviewOverlay />

            <Typography variant="h5" mb={2}>SO Attachment's </Typography>

            <Typography>
                <span style={{ fontSize: "16px", color: "#000" }}>RNO ID: {modalData?.rno_id}</span> |
                <span style={{ fontSize: "16px", color: "#000", marginLeft: "10px" }}>RM Name: {modalData?.userName}</span> |
                <span style={{ fontSize: "16px", color: "#000", marginLeft: "10px" }}>
                    Party Name: {modalData?.schoolName} ({modalData?.partyId})
                </span>
            </Typography>

            <IconButton
                aria-label="close"
                onClick={closePopup}
                sx={{ position: "absolute", right: 8, top: 8, color: (theme) => theme.palette.grey[500], zIndex: 1 }}
            >
                <CloseIcon />
            </IconButton>

            <Divider sx={{ my: 2, width: "100%", opacity: 1, borderTop: "1px solid #000" }} />

            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth disabled
                        label="Study Material Invoice No"
                        value={oldData[0]?.study_material_so_no || ""}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth disabled
                        label="Study Material Amount"
                        value={oldData[0]?.study_material_so_amount || ""}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth disabled
                        type="date"
                        label="Study Material Invoice Date"
                        value={
                            oldData[0]?.study_material_so_date
                                ? oldData[0].study_material_so_date.split("T")[0]
                                : ""
                        }
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
            </Grid>

            <Box mt={3}>
                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                    Uploaded Images
                </Typography>
                <AttachmentGrid
                    files={oldData?.[0]?.study_materia_so_attachment || []}
                    onRemove={null}
                    height="180px"
                />
            </Box>

        </Box>
    );
};

export default ViewSoImageModal;